import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, Receipt, BookOpen, NotebookPen, Library, BarChart3, Landmark, Settings, LogOut, Menu, X, Inbox, FileSignature, Repeat, Building2,
} from "lucide-react";
import { Banknote } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { FlagRule } from "@/components/common";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AIChat from "@/components/AIChat";
import CompanySwitcher from "@/components/CompanySwitcher";
import NotificationsBell from "@/components/NotificationsBell";
import { InstallButton, OfflineBar } from "@/components/Pwa";

const NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, end: true },
  { section: "salesSection" },
  { to: "/invoices", key: "invoices", icon: FileText },
  { to: "/quotes", key: "quotes", icon: FileSignature },
  { to: "/recurring", key: "recurring", icon: Repeat },
  { to: "/clients", key: "clients", icon: Users },
  { to: "/expenses", key: "expenses", icon: Receipt },
  { to: "/bank", key: "bank", icon: Landmark },
  { to: "/payroll", key: "payroll", icon: Banknote },
  { section: "accountingSection" },
  { to: "/accounts", key: "accounts", icon: BookOpen },
  { to: "/journal", key: "journal", icon: NotebookPen },
  { to: "/ledger", key: "ledger", icon: Library },
  { to: "/reports", key: "reports", icon: BarChart3 },
  { to: "/vat", key: "vat201", icon: Landmark },
  { section: "" },
  { to: "/settings", key: "settings", icon: Settings },
  { section: "adminSection", admin: true },
  { to: "/admin/contacts", key: "contactRequests", icon: Inbox, admin: true },
  { to: "/admin/formation", key: "formationRequests", icon: Building2, admin: true },
];

function Sidebar({ onNavigate }) {
  const { t } = useLang();
  const { user } = useAuth();
  return (
    <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
      {NAV.filter((n) => !n.admin || user?.role === "admin").map((n, i) =>
        n.section !== undefined ? (
          <div key={i} className="px-3 pb-2 pt-5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">{t(n.section)}</div>
        ) : (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.key}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-white/[0.08] text-white" : "text-white/60 hover:bg-white/[0.04] hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute inset-y-2 start-0 w-[3px] rounded-full bg-[#00A651]" />}
                <n.icon className={`h-4 w-4 ${isActive ? "text-[#00C462]" : ""}`} />
                {t(n.key)}
              </>
            )}
          </NavLink>
        )
      )}
    </nav>
  );
}

export default function Layout() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#F6F7F4]">
      {open && <div className="no-print fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}
      <aside
        className={`no-print fixed inset-y-0 start-0 z-50 flex w-64 flex-col bg-[#0B1220] text-white transition-transform lg:translate-x-0 rtl:lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"
        }`}
      >
        <div className="flex items-center justify-center px-4 pb-3 pt-5">
          <img src="/images/logo-compact-dark.png" alt="Mohassib" className="h-20 w-auto" data-testid="sidebar-logo" />
          <button className="ms-auto lg:hidden" data-testid="sidebar-close-btn" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <CompanySwitcher />
        <Sidebar onNavigate={() => setOpen(false)} />
        <div className="border-t border-white/10 p-4">
          <InstallButton className="mb-3 w-full justify-center" />
          <div className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="truncate text-xs text-white/45">{user?.company_name || user?.email}</div>
          <button data-testid="logout-btn" onClick={logout} className="mt-3 flex items-center gap-2 text-xs font-semibold text-white/60 transition-colors hover:text-[#FF5A6B]">
            <LogOut className="h-3.5 w-3.5" /> {t("logout")}
          </button>
        </div>
      </aside>
      <div className="lg:ps-64">
        <header className="no-print sticky top-0 z-30 bg-white/90 backdrop-blur-md">
          <FlagRule />
          <div className="flex h-14 items-center gap-3 border-b border-slate-200 px-4 sm:px-8">
            <button className="lg:hidden" data-testid="sidebar-open-btn" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <span className="hidden text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 md:inline">{t("tagline")}</span>
            <div className="ms-auto flex items-center gap-2"><NotificationsBell /><LanguageSwitcher /></div>
          </div>
          <OfflineBar />
        </header>
        <main className="print-reset mx-auto max-w-[1400px] p-4 pb-28 sm:p-8 lg:p-10">
          {user?.readonly && <div data-testid="readonly-banner" className="no-print mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">{t("readonlyBanner")} <span className="font-semibold">{user.viewer_email}</span></div>}
          <Outlet />
        </main>
      </div>
      <AIChat />
    </div>
  );
}
